import {useEffect, useRef} from 'react';

export const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Move initial focus into an open overlay (dialog, drawer, popover panel).
 *
 * @param {Object} options
 * @param {boolean} options.isOpen
 * @param {import('react').RefObject<HTMLElement>} [options.initialFocusRef]
 * @param {import('react').RefObject<HTMLElement>} [options.containerRef]
 * @param {boolean} [options.preferHeading=true]
 */
export function useOverlayInitialFocus({
  isOpen,
  initialFocusRef,
  containerRef,
  preferHeading = true,
}) {
  const headingRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const focusTarget =
          initialFocusRef?.current ??
          (preferHeading ?
            headingRef.current ??
            containerRef?.current?.querySelector('h1, h2, h3, [role="heading"]') :
            null) ??
          containerRef?.current?.querySelector(FOCUSABLE_SELECTOR);

        if (focusTarget && !focusTarget.hasAttribute('tabindex')) {
          focusTarget.setAttribute('tabindex', '-1');
        }

        focusTarget?.focus({preventScroll: true});
      });
    });

    return () => cancelAnimationFrame(id);
  }, [isOpen, initialFocusRef, containerRef, preferHeading]);

  return {headingRef};
}
